// 発注書の諸掛（送料・関税・その他）と landed cost（Stocky parity v2 Step 7）。
// 🔴 landed cost は保存されない派生値＝サーバが読み出しのたびに配賦し直す。
// 商品原価（variants.cost / cost_override）へは書き戻さない。
// プランゲートは Pro（`landed_cost`）。3本とも同じ形を返すので、書き込み後の
// 再取得は要らない。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type AdditionalCostKind = "shipping" | "duty" | "other";
export type AllocationMethod = "by_value" | "by_qty" | "by_volume";

export interface AdditionalCost {
  id: string;
  kind: AdditionalCostKind;
  amount: string;
  allocation: AllocationMethod;
  note: string | null;
}

export interface LandedCostLine {
  item_id: string;
  variant_id: string | null;
  sku: string | null;
  title: string | null;
  ordered_qty: number;
  unit_price: string;
  line_amount: string;
  allocated_amount: string;
  landed_line_amount: string;
  /** 数量0の行は null（0 だと「原価0円」と嘘になる）。 */
  landed_unit_cost: string | null;
  /**
   * 1個あたりの容積（m³・CBM）＝ variant の現在値（スナップショットではない）。
   * null＝未入力または variant 消滅＝容積比（by_volume）では配賦できない。
   */
  unit_volume_m3: string | null;
}

export interface LandedCostView {
  costs: AdditionalCost[];
  goods_total: string;
  additional_cost_total: string;
  /** 配賦できないときは null。 */
  landed_total: string | null;
  /** 配賦できない理由（null なら配賦済み）。一覧は必ず返るので削除して直せる。 */
  unallocatable_reason: string | null;
  lines: LandedCostLine[];
}

export interface AdditionalCostInput {
  kind: AdditionalCostKind;
  amount: string;
  allocation: AllocationMethod;
  note?: string | null;
}

function canonicalNullableId(value: unknown, label: string): string | null {
  if (value == null) return null;
  try {
    return canonicalStockyPublicBigIntId(value);
  } catch {
    throw new TypeError(`${label} is invalid`);
  }
}

export function normalizeLandedCostView(value: unknown): LandedCostView {
  if (value === null || typeof value !== "object") {
    throw new TypeError("landed cost response is invalid");
  }
  const view = value as Omit<LandedCostView, "costs" | "lines"> & {
    costs: unknown;
    lines: unknown;
  };
  if (!Array.isArray(view.costs) || !Array.isArray(view.lines)) {
    throw new TypeError("landed cost rows response is invalid");
  }
  return {
    ...view,
    costs: view.costs.map((value) => {
      if (value === null || typeof value !== "object") {
        throw new TypeError("additional cost response is invalid");
      }
      const cost = value as Omit<AdditionalCost, "id"> & { id: unknown };
      return { ...cost, id: canonicalStockyPublicBigIntId(cost.id) };
    }),
    lines: view.lines.map((value) => {
      if (value === null || typeof value !== "object") {
        throw new TypeError("landed cost line response is invalid");
      }
      const line = value as Omit<LandedCostLine, "item_id" | "variant_id"> & {
        item_id: unknown;
        variant_id: unknown;
      };
      return {
        ...line,
        item_id: canonicalStockyPublicBigIntId(line.item_id),
        variant_id: canonicalNullableId(line.variant_id, "variant_id"),
      };
    }),
  };
}

export async function getLandedCost(
  poId: StockyPublicBigIntId,
): Promise<LandedCostView> {
  const result = await apiFetch<unknown>(
    `/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/landed-cost`,
  );
  return normalizeLandedCostView(result);
}

export async function addAdditionalCost(
  poId: StockyPublicBigIntId,
  input: AdditionalCostInput,
): Promise<LandedCostView> {
  // 🔴 `body` はオブジェクトのまま渡す＝`apiFetch` が中で JSON.stringify する。
  //    ここで先に文字列化すると二重エンコードになり、サーバは JSON 文字列を
  //    受け取って必ず 422（Input should be a valid dictionary or object）になる。
  const result = await apiFetch<unknown>(`/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/additional-costs`, {
    method: "POST",
    body: input,
  });
  return normalizeLandedCostView(result);
}

export async function deleteAdditionalCost(
  poId: StockyPublicBigIntId,
  costId: StockyPublicBigIntId,
): Promise<LandedCostView> {
  const result = await apiFetch<unknown>(
    `/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/additional-costs/${canonicalStockyPublicBigIntId(costId)}`,
    { method: "DELETE" },
  );
  return normalizeLandedCostView(result);
}
